import { CHAT_WIDGET, readPeoplePageSignals } from "./people-signals";
import type { CheckResult, SiteSnapshot } from "./types";

const SPEC = "https://www.siteglue.ai/audits/conversion-optimization";
const SITEGLUE_WIDGET =
  /(?:easygpt\.builders|siteglue\.ai)\/w(?:-v2)?\.js|\/w\.js\/?\?id=/i;
const BROKEN_FORM_ACTION = /^(javascript:|#|)$/i;

export const CRO_FLOW_CATEGORY = "Conversion Flow";

export const CONVERSION_FLOW_CHECK_IDS = [
  "CF-ENTRY",
  "CF-INTERACT",
  "CF-INTENT",
  "CF-CAPTURE",
  "CF-ACTION",
  "CF-HANDOFF",
  "CF-CONFIRM",
  "CF-DEADEND",
  "CF-CONTEXT",
  "CF-BROKEN",
] as const;

export type ConversionFlowCheckId = (typeof CONVERSION_FLOW_CHECK_IDS)[number];

export const FLOW_TITLES: Record<ConversionFlowCheckId, string> = {
  "CF-ENTRY": "Usable conversion entry",
  "CF-INTERACT": "Visitor can start the interaction",
  "CF-INTENT": "Request is acknowledged",
  "CF-CAPTURE": "Information capture without repeats",
  "CF-ACTION": "Scheduling or requested action",
  "CF-HANDOFF": "Human handoff when needed",
  "CF-CONFIRM": "Confirmation of the outcome",
  "CF-DEADEND": "No dead-end path",
  "CF-CONTEXT": "Context is kept across steps",
  "CF-BROKEN": "Links and submissions work",
};

function flowCheck(
  id: ConversionFlowCheckId,
  status: CheckResult["status"],
  evidence: string,
  pageUrl: string,
  extras?: Partial<CheckResult>,
): CheckResult {
  return {
    id,
    title: FLOW_TITLES[id],
    category: CRO_FLOW_CATEGORY,
    specUrl: SPEC,
    maturity: "emerging",
    detection: extras?.detection ?? "html",
    weight: 1,
    impact: "Visitors should complete a request without losing context or hitting a dead end.",
    status,
    evidence,
    verification:
      status === "passed"
        ? "verified"
        : status === "failed"
          ? "not_detected"
          : status === "not_applicable"
            ? "not_applicable"
            : "not_verified",
    confidence: extras?.confidence ?? "low",
    pageUrl,
  };
}

function notVerified(id: ConversionFlowCheckId, evidence: string, pageUrl: string, detection: CheckResult["detection"] = "html"): CheckResult {
  return flowCheck(id, "not_tested", evidence, pageUrl, { detection, confidence: "low" });
}

function formActions(html: string): string[] {
  return [...html.matchAll(/<form\b[^>]*action=["']([^"']*)["'][^>]*>/gi)].map((match) => match[1] ?? "");
}

function formsWithoutAction(html: string): boolean {
  return /<form\b(?![^>]*\baction=)/i.test(html);
}

export function siteGlueWidgetPresent(html: string): boolean {
  return SITEGLUE_WIDGET.test(html);
}

export function evaluateConversionFlow(snapshot: SiteSnapshot): CheckResult[] {
  const pageUrl = snapshot.finalUrl;
  const html = snapshot.html || snapshot.homepage.body || "";
  const readable = snapshot.homepage.status >= 200 && snapshot.homepage.status < 400 && html.trim().length > 0;
  if (!readable) {
    return CONVERSION_FLOW_CHECK_IDS.map((id) =>
      notVerified(id, "The page could not be read. Conversion Flow was not verified.", pageUrl),
    );
  }

  const signals = readPeoplePageSignals(html);
  const hasEntry = signals.form || signals.chat || signals.tel || signals.mail || signals.contactPage || signals.schedule;
  const actions = formActions(html);
  const brokenForm = signals.form && (formsWithoutAction(html) || actions.some((action) => BROKEN_FORM_ACTION.test(action.trim())));
  const widget = CHAT_WIDGET.test(html);
  const siteGlue = siteGlueWidgetPresent(html);

  const entryEvidence = [
    signals.form ? "form" : null,
    signals.chat ? "chat widget" : null,
    signals.tel ? "phone link" : null,
    signals.mail ? "email" : null,
    signals.schedule ? "scheduling link" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const laterNv =
    "Entry was detected. Later Conversion Flow steps were not executed on this scan, so they stay not verified.";

  return [
    hasEntry
      ? flowCheck("CF-ENTRY", "passed", `Entry detected (${entryEvidence || "contact path"}). Later steps were not verified from this alone.`, pageUrl, {
          confidence: "medium",
        })
      : flowCheck("CF-ENTRY", "failed", "No form, chat, phone, email, or booking path was found on the public page.", pageUrl, {
          confidence: "medium",
        }),
    notVerified(
      "CF-INTERACT",
      siteGlue
        ? "A SiteGlue widget is on the page. Live chat was not executed on this scan, so interaction stays not verified."
        : widget
          ? "A chat widget is on the page. The scan did not open it, so interaction stays not verified."
          : signals.form
            ? "A form is on the page. The scan did not submit it, so interaction stays not verified."
            : laterNv,
      pageUrl,
      widget || siteGlue ? "browser" : "html",
    ),
    notVerified("CF-INTENT", laterNv, pageUrl, "browser"),
    notVerified("CF-CAPTURE", laterNv, pageUrl, "browser"),
    notVerified(
      "CF-ACTION",
      signals.schedule
        ? "A scheduling link is present. Booking was not completed, so this step stays not verified."
        : laterNv,
      pageUrl,
    ),
    notVerified(
      "CF-HANDOFF",
      signals.handoff
        ? "Handoff language is on the page. Routing was not executed, so this step stays not verified."
        : laterNv,
      pageUrl,
    ),
    notVerified("CF-CONFIRM", laterNv, pageUrl, "browser"),
    notVerified("CF-DEADEND", "Dead ends are only failed when a path was actually attempted and stopped.", pageUrl, "browser"),
    notVerified("CF-CONTEXT", "Repeated questions are only failed when a live path re-asked known facts.", pageUrl, "browser"),
    brokenForm
      ? flowCheck(
          "CF-BROKEN",
          "failed",
          "A form is missing a usable action URL. That is a broken submission path.",
          pageUrl,
          { confidence: "medium" },
        )
      : notVerified(
          "CF-BROKEN",
          "Broken links and failed submissions are only failed when those requests were actually attempted.",
          pageUrl,
          "html",
        ),
  ];
}

export function conversionFlowBeyondEntry(checks: CheckResult[]): CheckResult[] {
  return checks.filter((check) => check.id.startsWith("CF-") && check.id !== "CF-ENTRY");
}

export function conversionFlowHasConclusiveLaterStep(checks: CheckResult[]): boolean {
  return conversionFlowBeyondEntry(checks).some(
    (check) => check.status === "passed" || check.status === "failed" || check.status === "warning",
  );
}
